import { getTimezoneOffset } from 'date-fns-tz'
import timezone from '../data/timezone'
import { HourFormatEnum } from '../types/user'

export const getTimezoneText = (tz: string | undefined | null): string => {
  if (!tz) {
    return ''
  }
  const foundZone = timezone.find((timezone) => timezone.id === tz)
  return foundZone ? foundZone.text : ''
}

/**
 * Finds a similar timezone from our list for a given unknown timezone
 * Uses offset matching to find a timezone with the same GMT offset
 * Prefers timezones from the same geographic region and with similar DST behavior
 *
 * @param unknownTimezone - The timezone string to find a match for
 * @returns Object containing the matched timezone id and hourFormat, or null if no match found
 *
 * @example
 * findSimilarTimezone('America/Detroit')
 * // Returns: { id: 'America/New_York', hourFormat: HourFormatEnum.twelve }
 */
export const findSimilarTimezone = (unknownTimezone: string): { id: string; hourFormat: HourFormatEnum } | null => {
  if (!unknownTimezone || unknownTimezone.trim() === '') {
    return null
  }

  try {
    const now = new Date()
    const unknownOffset = getTimezoneOffset(unknownTimezone, now)

    // Extract region prefix (e.g., 'America' from 'America/Detroit')
    const unknownRegion = unknownTimezone.split('/')[0]

    // Check if unknown timezone observes DST by checking offset in summer vs winter
    const summerDate = new Date(now.getFullYear(), 6, 1) // July 1st
    const winterDate = new Date(now.getFullYear(), 0, 1) // January 1st
    let unknownHasDST = false
    try {
      const summerOffset = getTimezoneOffset(unknownTimezone, summerDate)
      const winterOffset = getTimezoneOffset(unknownTimezone, winterDate)
      unknownHasDST = summerOffset !== winterOffset
    } catch {
      // If we can't determine DST, assume it has DST
      unknownHasDST = true
    }

    // Find all timezones from our list with the same offset
    const matchingTimezones = timezone.filter((tz) => {
      try {
        return getTimezoneOffset(tz.id, now) === unknownOffset
      } catch {
        return false
      }
    })

    if (matchingTimezones.length === 0) {
      return null
    }

    // Score each matching timezone
    const scoredTimezones = matchingTimezones.map((tz) => {
      let score = 0

      // Check if it's from the same region (+10 points)
      if (tz.id.startsWith(unknownRegion + '/')) {
        score += 10
      }

      // Check DST behavior (+5 points for matching)
      try {
        const tzSummerOffset = getTimezoneOffset(tz.id, summerDate)
        const tzWinterOffset = getTimezoneOffset(tz.id, winterDate)
        const tzHasDST = tzSummerOffset !== tzWinterOffset

        if (tzHasDST === unknownHasDST) {
          score += 5
        }
      } catch {
        // Ignore if we can't check DST
      }

      return { ...tz, score }
    })

    // Sort by score (highest first)
    scoredTimezones.sort((a, b) => b.score - a.score)

    // Find the first timezone with hourFormat defined
    const bestMatch = scoredTimezones.find((tz) => tz.hourFormat)
    if (bestMatch) {
      return { id: bestMatch.id, hourFormat: bestMatch.hourFormat }
    }
  } catch (error) {
    // Failed to find similar timezone - fall through to return null without logging
  }

  return null
}

/**
 * Normalizes a timezone to one from our supported list
 * If the timezone exists in our list, returns it as-is
 * If not found, attempts to find a similar timezone by offset
 * Falls back to 'Etc/GMT' if no match is found
 *
 * @param timezoneId - The timezone string to normalize
 * @returns A timezone string from our supported list
 *
 * @example
 * normalizeTimezone('America/Detroit') // Returns: 'America/New_York'
 * normalizeTimezone('Europe/Belgrade') // Returns: 'Europe/Belgrade' (already in list)
 * normalizeTimezone('Invalid/Timezone') // Returns: 'Etc/GMT' (fallback)
 */
export const normalizeTimezone = (timezoneId: string): string => {
  // Check if it already exists in our list
  if (timezone.find((tz) => tz.id === timezoneId)) {
    return timezoneId
  }

  // Try to find a similar timezone by offset
  const similar = findSimilarTimezone(timezoneId)
  return similar ? similar.id : 'Etc/GMT'
}

export const getTimeZoneOrDefaultToUtc = (): string => {
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  if (!browserTimezone) return 'Etc/GMT'

  // Normalize to a known timezone from our list
  return normalizeTimezone(browserTimezone)
}
